import { createStore, applyMiddleware, Store } from 'redux';
import createSagaMiddleware from 'redux-saga';

// Persist
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { createFilter } from 'redux-persist-transform-filter';

import { createBrowserHistory } from 'history';

// Middlewares
import { routerMiddleware } from 'connected-react-router';
import { promiseMiddleware } from 'store/utils/promiseAction';

// Types
import { AttendanceState } from './ducks/attendance/types';
import { BugReportsState } from './ducks/bugReports/types';
import { FleetState } from './ducks/fleet/types';
import { DbdsState } from './ducks/dbds/types';
import { EmployeesState } from './ducks/employees/types';
import { ItinerariesState } from './ducks/itineraries/types';
import { LinesState } from './ducks/lines/types';
import { BenefitsState } from './ducks/benefits/types';
import { ProfilesState } from './ducks/profiles/types';
import { LotsState } from './ducks/lots/types';
import { OperatorsState } from './ducks/operators/types';
import { SnackbarsState } from './ducks/snackbars/types';
import { SchemasState } from './ducks/schemas/types';
import { StopsState } from './ducks/stops/types';
import { TransportOperatorsState } from './ducks/transportOperators/types';
import { SupervisorsGroupsState } from './ducks/supervisorsGroups/types';
import { UsersState } from './ducks/users/types';
import { CompaniesState } from './ducks/companies/types';
import { CollaboratorsState } from './ducks/collaborators/types';
import { CollaboratorsGroupsState } from './ducks/collaboratorsGroups/types';
import { OrdersState } from './ducks/orders/types';
import { OrdersSchoolState } from './ducks/ordersSchool/types';
import { SchoolSettingsState } from './ducks/schoolSettings/types';
import { TransportCardsState } from './ducks/transportCards/types';
import { DenyListState } from './ducks/denylist/types';
import { UserProfileState } from './ducks/userProfile/types';
import { UtilsState } from './ducks/utils/types';
import { SchoolsState } from './ducks/schools/types';
import { ServiceDiscoveryState } from './ducks/serviceDiscovery/types';
import { StudentsState } from './ducks/students/types';
import { ReportsState } from './ducks/reports/types';
import { GarageState } from './ducks/garage/types';
import { SellingPointsState } from './ducks/sellingPoints/types';
import { SellingTerminalsState } from './ducks/sellingTerminals/types';
import { TripsState } from './ducks/trips/types';
import { DrawerState } from './ducks/drawer/types';
import { EmployeesSalesState } from './ducks/employeesSales/types';
import { CollapseState } from './ducks/collapse/types';
import { TerminalsState } from './ducks/terminals/types';
import { TerminalBlocksState } from './ducks/terminalBlocks/types';
import { UserIpsState } from './ducks/userIps/types';
import { ModalFilterState } from './ducks/modalFilter/types';
import { GroupingModalState } from './ducks/groupingModal/types';
import { SearchsState } from './ducks/searchs/types';
import { AppBarState } from './ducks/appBar/types';
import { ListingDrawerState } from './ducks/listingDrawer/types';
import { LayoutState } from './ducks/layout/types';
import { RouteState } from './ducks/routes/types';
import { FacialState } from './ducks/facial/types';
import { WalletHistoryState } from './ducks/wallet/types';
import { balanceState } from './ducks/balance/types';
import { DevicesState } from './ducks/devices/types';
import { ElementRefState } from './ducks/elementRef/types';
import { SupervisorsState } from './ducks/supervisors/types';
import { TransportCardsPlaceState } from './ducks/transportCardsPlace/types';
import { FaresState } from './ducks/fares/types';
import { DatesState } from './ducks/dates/types';
import { LineLotState } from './ducks/lineLot/types';
import { TollState } from './ducks/tolls/types';
import { IntegrationFenceState } from './ducks/integrationFence/types';
import { SchedulesMatrixState } from './ducks/schedulesMatrix/types';
import { PointsListState } from './ducks/pointsList/types';
import { AudioState } from './ducks/audio/types';
import { LinesMatrixIntegrationState } from './ducks/linesMatrixIntegration/types';
import { CompanyUsersState } from './ducks/companyUsers/types';
import { CompanyReportState } from './ducks/company/types';
import { AppUserMergeState } from './ducks/appUserMerge/types';
import { DialogsState } from './ducks/dialogs/types';
import { NotificationsState } from './ducks/notifications/types';
import { SegmentationsState } from './ducks/segmentations/types';
import { VoucherState } from './ducks/voucher/types';
import { TurnstilesState } from './ducks/turnstiles/types';
import { ProductState } from './ducks/product/types';
import { FareBonusState } from './ducks/fareBonus/types';
import { FareTaxState } from './ducks/fareTax/types';
import { CurrentModuleState } from './ducks/currentModule/types';
import { SynopticState } from './ducks/synoptic/types';
import { TaxState } from './ducks/tax/types';
import { ScheduleState } from './ducks/schedule/types';
import { publicStoreState } from './ducks/publicStore/types';

import createRootReducer from './ducks/rootReducer';
import rootSaga from './ducks/rootSaga';

export const history = createBrowserHistory();

export interface ApplicationState {
  attendance: AttendanceState;
  fleet: FleetState;
  lines: LinesState;
  benefits: BenefitsState;
  profiles: ProfilesState;
  lots: LotsState;
  operators: OperatorsState;
  snackbars: SnackbarsState;
  schemas: SchemasState;
  stops: StopsState;
  transportOperators: TransportOperatorsState;
  supervisorsGroups: SupervisorsGroupsState;
  users: UsersState;
  dbds: DbdsState;
  bugReports: BugReportsState;
  employees: EmployeesState;
  employeesSales: EmployeesSalesState;
  denylist: DenyListState;
  transportCards: TransportCardsState;
  userProfile: UserProfileState;
  utils: UtilsState;
  schools: SchoolsState;
  serviceDiscovery: ServiceDiscoveryState;
  students: StudentsState;
  orders: OrdersState;
  ordersSchool: OrdersSchoolState;
  schoolSettings: SchoolSettingsState;
  companies: CompaniesState;
  collaborators: CollaboratorsState;
  collaboratorsGroups: CollaboratorsGroupsState;
  itineraries: ItinerariesState;
  reports: ReportsState;
  garage: GarageState;
  sellingPoints: SellingPointsState;
  sellingTerminals: SellingTerminalsState;
  trips: TripsState;
  drawer: DrawerState;
  collapse: CollapseState;
  terminals: TerminalsState;
  terminalBlocks: TerminalBlocksState;
  userIps: UserIpsState;
  modalFilter: ModalFilterState;
  groupingModal: GroupingModalState;
  searchs: SearchsState;
  appBar: AppBarState;
  listingDrawer: ListingDrawerState;
  layout: LayoutState;
  routes: RouteState;
  facial: FacialState;
  wallet: WalletHistoryState;
  balance: balanceState;
  devices: DevicesState;
  elementRef: ElementRefState;
  supervisors: SupervisorsState;
  transportCardsPlace: TransportCardsPlaceState;
  fares: FaresState;
  dates: DatesState;
  lineLot: LineLotState;
  tolls: TollState;
  integrationFence: IntegrationFenceState;
  schedulesMatrix: SchedulesMatrixState;
  pointsList: PointsListState;
  audio: AudioState;
  linesMatrixIntegration: LinesMatrixIntegrationState;
  companyUsers: CompanyUsersState;
  companyReport: CompanyReportState;
  appUserMerge: AppUserMergeState;
  dialogs: DialogsState;
  notifications: NotificationsState;
  segmentations: SegmentationsState;
  voucher: VoucherState;
  turnstiles: TurnstilesState;
  product: ProductState;
  fareBonus: FareBonusState;
  fareTax: FareTaxState;
  synoptic: SynopticState;
  tax: TaxState;
  schedule: ScheduleState;
  publicStore: publicStoreState;
  currentModule: CurrentModuleState;
}

const userFirebaseDataFilter = createFilter('users', ['firebaseData']);

const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'transportOperators',
    'users',
    'lastVersion',
    'localLimit',
    'publicStore',
    'currentModule',
    'drawer',
  ],
  transforms: [userFirebaseDataFilter],
};

const persistedReducer = persistReducer(
  persistConfig,
  createRootReducer(history),
);

const sagaMiddleware = createSagaMiddleware();
const store: Store<ApplicationState> = createStore(
  persistedReducer,
  applyMiddleware(promiseMiddleware, sagaMiddleware, routerMiddleware(history)),
);

// REDUX DEV TOOLS
/* const store: Store<ApplicationState> = createStore(
  persistedReducer,
  compose(
    applyMiddleware(promiseMiddleware, sagaMiddleware, routerMiddleware(history)),
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
      (window as any).__REDUX_DEVTOOLS_EXTENSION__(),
  ),
); */

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);
export default store;
